import moment from "moment";
import * as yup from "yup";
import { toast } from "react-toastify";
import { parsePhoneNumberFromString } from "libphonenumber-js/min";
import metadata from "libphonenumber-js/metadata.min.json";
import { LOGOUT_USER, SET_PERMISSIONS } from "../redux/actions/AuthActions";
import { getSupportedLanguages } from "./languageService";
import { Box } from "@mui/material";
import { navigateTo } from "../redux/actions/NavigationActions";
import { stripShellPrefix, ensureAdminShellPath } from "../config/routePrefixes";

/** Prefer prefixed displayOrderNumber (del-/to-/dine-) over numeric orderNumber — matches POS. */
export const getOrderDisplayNumber = (order) => {
  if (order == null || typeof order !== "object") return "";
  return order.displayOrderNumber ?? order.orderNumber ?? "";
};

export function isMobile() {
  if (window) {
    return window.matchMedia(`(max-width: 767px)`).matches;
  }
  return false;
}

export function isMdScreen() {
  if (window) {
    return window.matchMedia(`(max-width: 1199px)`).matches;
  }
  return false;
}

function currentYPosition() {
  if (!window) {
    return 0;
  }
  // Firefox, Chrome, Opera, Safari
  if (window.pageYOffset) return window.pageYOffset;
  // Internet Explorer 6 - standards mode
  if (document.documentElement && document.documentElement.scrollTop)
    return document.documentElement.scrollTop;
  // Internet Explorer 6, 7 and 8
  if (document.body.scrollTop) return document.body.scrollTop;
  return 0;
}

function elmYPosition(elm) {
  var y = elm.offsetTop;
  var node = elm;
  while (node.offsetParent && node.offsetParent !== document.body) {
    node = node.offsetParent;
    y += node.offsetTop;
  }
  return y;
}

export function scrollTo(scrollableElement, elmID) {
  var elm = document.getElementById(elmID);
  if (!elmID || !elm) {
    return;
  }
  var startY = currentYPosition();
  var stopY = elmYPosition(elm);
  var distance = stopY > startY ? stopY - startY : startY - stopY;
  if (distance < 100) {
    scrollTo(0, stopY);
    return;
  }
  var speed = Math.round(distance / 50);
  if (speed >= 20) speed = 20;
  var step = Math.round(distance / 25);
  var leapY = stopY > startY ? startY + step : startY - step;
  var timer = 0;
  if (stopY > startY) {
    for (var i = startY; i < stopY; i += step) {
      setTimeout(
        (function (leapY) {
          return () => {
            scrollableElement.scrollTo(0, leapY);
          };
        })(leapY),
        timer * speed
      );
      leapY += step;
      if (leapY > stopY) leapY = stopY;
      timer++;
    }
    return;
  }
  for (let i = startY; i > stopY; i -= step) {
    setTimeout(
      (function (leapY) {
        return () => {
          scrollableElement.scrollTo(0, leapY);
        };
      })(leapY),
      timer * speed
    );
    leapY -= step;
    if (leapY < stopY) leapY = stopY;
    timer++;
  }
  return;
}

export function getTimeDifference(date) {
  let difference =
    moment(new Date(), "DD/MM/YYYY HH:mm:ss").diff(
      moment(date, "DD/MM/YYYY HH:mm:ss")
    ) / 1000;

  if (difference < 60) return `${Math.floor(difference)} seconds`;
  else if (difference < 3600) return `${Math.floor(difference / 60)} minutes`;
  else if (difference < 86400) return `${Math.floor(difference / 3660)} hours`;
  else if (difference < 86400 * 30)
    return `${Math.floor(difference / 86400)} days`;
  else if (difference < 86400 * 30 * 12)
    return `${Math.floor(difference / 86400 / 30)} months`;
  else return `${(difference / 86400 / 30 / 12).toFixed(1)} years`;
}

export function generateRandomId() {
  let tempId = Math.random().toString();
  let uid = tempId.substr(2, tempId.length - 1);
  return uid;
}

export function getPagePermission(allPages, pageKey) {
  let final
  if (!Array.isArray(allPages)) return final
  allPages.map((page) => {
    if (page.key === pageKey)
      return final = page
    if (!final && page.subPage?.length > 0)
      final = getPagePermission(page.subPage, pageKey)
  })
  return final
}

export function getPagePermissionByPath(allPages, path) {
  const normalizedPath = stripShellPrefix(path || '') || path

  function pathToRegex(route) {
    // Replace :param with a regex that matches any non-slash characters
    return new RegExp('^' + route.replace(/:[^\s/]+/g, '([^\\/]+)') + '$');
  }

  let final;
  if (!Array.isArray(allPages)) return final
  allPages.some((page) => {
    if (typeof page.route === 'string' && pathToRegex(page.route).test(normalizedPath)) {
      final = page;
      return true; // Exit loop when page is found
    }
    if (!final && page.subPage?.length > 0) {
      final = getPagePermissionByPath(page.subPage, normalizedPath); // Recursive call
      return !!final; // Exit loop if final is found in subPage
    }
    return false; // Continue iterating
  });
  return final;
}



export function getQueryParam(prop) {
  var params = {};
  var search = decodeURIComponent(
    window.location.href.slice(window.location.href.indexOf("?") + 1)
  );
  var definitions = search.split("&");
  definitions.forEach(function (val) {
    var parts = val.split("=", 2);
    params[parts[0]] = parts[1];
  });
  return prop && prop in params ? params[prop] : params;
}

export function classList(classes) {
  return Object.entries(classes)
    .filter((entry) => entry[1])
    .map((entry) => entry[0])
    .join(" ");
}

export function initCodeViewer() {
  if (!document) return;
  const pre = document.getElementsByTagName("pre");
  if (!pre.length) return;
  Array.prototype.map.call(pre, (p) => {
    p.classList.add("collapsed");
    p.addEventListener("click", (e) => {
      e.target.classList.remove("collapsed");
    });
  });
}

export const reformatArray = (arr, formats, language) => {
  let final = [];
  if (arr?.length > 0 && formats?.length > 0) {
    arr.map((zone) => {
      let obj = {};
      formats?.map((format) => {
        let temp = format[1].split(".");
        let field;
        if (temp.length > 1) {
          for (let i = 0; i < temp.length; i++) {
            if (i === 0) field = zone[temp[i]];
            else field = field[temp[i]];
          }
        } else field = zone[format[1]];
        if (language && field && typeof field === 'object' && !Array.isArray(field)) {
          obj[format[0]] = getFieldData(zone, format[1], language);
        } else obj[format[0]] = field;
        return 0;
      });
      final.push(obj);
      return 0;
    });
  }
  return final;
};

/** Merged dictionaries, per `languages` array and page — so a merged one keeps its identity between renders. */
const pageDictionaryCache = new WeakMap();

/** Is `entry` a value Excel turned into a number on the CSV → xlsx step ("1-30" → 46052, a date → its serial)? */
const isExcelNumber = (entry) => typeof entry?.english === 'number';

/**
 * Folds every Language row of one page into one dictionary. A later row wins on a key both rows hold
 * (what this function returned when it kept only the last row), except that a later value Excel
 * turned into a number yields to an earlier row's text.
 */
const mergePageRows = (rows) => {
  const merged = {};
  rows.forEach((row) => {
    Object.entries(row?.values || {}).forEach(([key, entry]) => {
      if (merged[key] && !isExcelNumber(merged[key]) && isExcelNumber(entry)) return;
      merged[key] = entry;
    });
  });
  return merged;
};

/**
 * The dictionary of one page. A page can hold two Language rows on one platform — the CSV importer
 * writes new keys into either of them — so every row of the page is merged; reading only the last
 * one showed "No translation" for every key that landed in the other.
 */
export const getTranslation = (pageName, data) => {
  const languages = data?.[0]?.languages;
  if (!Array.isArray(languages)) return {};
  let byPage = pageDictionaryCache.get(languages);
  if (!byPage) {
    byPage = new Map();
    pageDictionaryCache.set(languages, byPage);
  }
  if (!byPage.has(pageName)) {
    const rows = languages.filter((zone) => zone.page === pageName);
    byPage.set(pageName, rows.length > 1 ? mergePageRows(rows) : rows[0]?.values || {});
  }
  return byPage.get(pageName);
};

export const getField = (dictionary, fieldName, language, fallback) => {
  if (!dictionary || Object.keys(dictionary).length === 0) return fallback || "";
  if (!dictionary[fieldName]) return fallback || 'No translation';
  if (!dictionary[fieldName][language] && !dictionary[fieldName]['english']) return fallback || "No translation";
  return (
    dictionary[fieldName][language] || dictionary[fieldName]['english'] || fallback || ""
  );
};

/**
 * `getField` returns the literal string "No translation" for a missing key, which is truthy — so
 * `getField(...) || fallback` never falls back. Same helper as adm-stock-management's (the customer
 * receipt design page ported from there uses it).
 */
export const tField = (dictionary, key, language, fallback) => {
  const value = getField(dictionary, key, language);
  if (!value || value === 'No translation') return fallback || '';
  return value;
};

export const normalizeDigits = (value) => String(value || "").replace(/\D/g, "");

export const getCountryMetaByPhoneOrIso = (globalConfig = [], countryCode = "", countryIsoCode = "") => {
  const normalizedDialCode = normalizeDigits(countryCode);
  const iso = String(countryIsoCode || "").trim().toUpperCase();
  const list = Array.isArray(globalConfig) ? globalConfig : [];

  if (iso) {
    const byIso = list.find((c) => String(c?.code || "").toUpperCase() === iso);
    if (byIso) return byIso;
  }

  return list.find((c) => normalizeDigits(c?.phone) === normalizedDialCode) || null;
};

export const validatePhoneNumberByCountry = ({
  number,
  countryCode,
  countryIsoCode,
  globalConfig = [],
  invalidPhoneText = "Phone number length is invalid for",
}) => {
  const digits = normalizeDigits(number);
  const selectedCountry = getCountryMetaByPhoneOrIso(globalConfig, countryCode, countryIsoCode);
  const iso = String(selectedCountry?.code || countryIsoCode || "").trim().toUpperCase();
  const dialCode = normalizeDigits(countryCode || selectedCountry?.phone);
  const countryLabel = selectedCountry?.countryName || iso || countryCode || "";

  try {
    const parsed = iso
      ? parsePhoneNumberFromString(digits, iso)
      : dialCode
        ? parsePhoneNumberFromString(`+${dialCode}${digits}`)
        : null;

    if (!parsed || !parsed.isPossible()) {
      return { valid: false, message: `${invalidPhoneText} ${countryLabel}`.trim() };
    }

    return { valid: true, message: "" };
  } catch (e) {
    return { valid: false, message: `${invalidPhoneText} ${countryLabel}`.trim() };
  }
};

export const getPhoneMaxLengthByCountry = ({
  countryCode,
  countryIsoCode,
  globalConfig = [],
  defaultMaxLength = 15,
}) => {
  const selectedCountry = getCountryMetaByPhoneOrIso(globalConfig, countryCode, countryIsoCode);
  const iso = String(selectedCountry?.code || countryIsoCode || "").trim().toUpperCase();
  const countryMeta = metadata?.countries?.[iso];
  const possibleLengths = Array.isArray(countryMeta?.[3]) ? countryMeta[3] : [];
  const maxLength = possibleLengths.length ? Math.max(...possibleLengths) : defaultMaxLength;
  return Number.isFinite(maxLength) ? maxLength : defaultMaxLength;
};

const getFirstAvailableFieldValue = (field) => {
  if (!field || typeof field !== 'object') {
    return undefined;
  }
  const value = Object.values(field).find((entry) => typeof entry === 'string' && entry.trim());
  return value || undefined;
};

export const getFieldData = (data, fieldName, language, emptyFieldMessage) => {
  const message = typeof emptyFieldMessage === 'string' ? emptyFieldMessage : "";

  const supportedLanguages = getSupportedLanguages();
  const defaultLanguage = supportedLanguages?.find((lang) => lang.isDefault)?.name;

  let temp = fieldName?.split('.');
  let field;

  temp?.map((tmp, i) => {
    if (i === 0) field = data?.[tmp];
    else field = field?.[tmp];
  });

  if (!fieldName) field = data;
  if (!data) return message || '';
  if (!field) return message || '';
  if (typeof field === 'string') return field;

  const fallbackValue = getFirstAvailableFieldValue(field);
  if (!field[language] && !field[defaultLanguage] && !fallbackValue) return message || '';
  return field[language] || field[defaultLanguage] || fallbackValue || message || "";
};

export const generateMultipleLangsValidation = (
  supportedLanguages,
  translation
) => {
  let final = {};
  supportedLanguages?.map((language, i) => {
    final[language.name] =
      language.isDefault
        ? yup.string().required(translation || "")
        : yup.string().optional();
    return 0;
  });
  return yup.object().shape(final);
};

export const generateMultipleLangsValidationWithSpecialCharacters = (
  supportedLanguages,
  requiredText,
  specialCharsText,
  forbiddenChars
) => {
  // If no forbidden chars provided, behave like generateMultipleLangsValidation
  if (!forbiddenChars || !Array.isArray(forbiddenChars) || forbiddenChars.length === 0) {
    return generateMultipleLangsValidation(supportedLanguages, requiredText);
  }

  let final = {};
  supportedLanguages?.forEach((language) => {
    final[language.name] = language.isDefault
      ? yup.string()
        .required(requiredText || "")
        .test(
          'no-special-chars',
          specialCharsText || "",
          (value) => {
            if (!value) return true;
            return !forbiddenChars.some(char => value.includes(char));
          }
        )
      : yup.string()
        .test(
          'no-special-chars',
          `${language.name}: ${specialCharsText}` || "",
          (value) => {
            if (!value) return true;
            return !forbiddenChars.some(char => value.includes(char));
          }
        )
  });
  return yup.object().shape(final);
};

export const getInitialState = (supportedLanguages, type, data, fallBack, def) => {
  let final = {};
  let defaultLanguage = supportedLanguages.find((lang) => lang.isDefault)
  supportedLanguages?.map((language) => {
    final[language.name] =
      type === 0
        ? language.name === defaultLanguage.name && def ? def : ""
        : data
          ? data[language.name]
            ? data[language.name]
            : ""
          : fallBack
            ? fallBack[language.name] || ""
            : "";
    return 0;
  });
  return final;
};

export const getInitialStateArray = (supportedLanguages, keys, data) => {
  let final = []
  data.map((row) => {
    let obj = { ...row }
    keys.map((key) => {
      obj[key] = getInitialState(supportedLanguages, 1, obj[key])
    })
    final.push(obj)
  })
  return final
}

export const generateFields = (supportedLanguages, field, fieldProps) => {
  return supportedLanguages?.map((language, i) => {
    return (
      <Box sx={{ display: 'flex' }}>
        {field(fieldProps(language.name), i, language)}
        <div id={`portal-target-${language.name}`}></div>
      </Box>
    );
  });
};

export const getFieldName = (supportedLanguages, fieldKey) => {
  const defaultLanguage = supportedLanguages.find((lang) => lang.isDefault)
  if (defaultLanguage)
    return fieldKey + `.${defaultLanguage.name}`
}

export const getFieldValue = (supportedLanguages, field) => {
  const defaultLanguage = supportedLanguages.find((lang) => lang.isDefault)
  if (defaultLanguage && field)
    return field[defaultLanguage.name]
}

export const setFieldValue = (supportedLanguages, setFieldValue, fieldKey, newValue) => {
  const defaultLanguage = supportedLanguages.find((lang) => lang.isDefault)
  setFieldValue(`${fieldKey}.${defaultLanguage.name}`, newValue)
}

export const removeEmptyLang = (supportedLanguages, field) => {
  let final = field;
  supportedLanguages?.map((language) => {
    if (final[language.name] === "") delete final[language.name];
    return 0;
  });
  return final;
};

export function slugify(text) {
  return text
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-');
}

export const getTitleCase = (str) => {
  if (str) {
    const titleCase = str
      .toLowerCase()
      .split(" ")
      .map((word) => {
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(" ");

    return titleCase;
  }
  return " ";
};

export const getParagraphCase = (inputStr) => {
  if (inputStr) {
    let words = inputStr.toLowerCase().split(" ");
    words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1);
    for (let i = 1; i < words.length; i++) {
      if (/[\.\?!]\s*/.test(words[i - 1])) {
        words[i] = words[i].charAt(0).toUpperCase() + words[i].slice(1);
      } else {
        words[i] = words[i].toLowerCase();
      }
    }
    return words.join(" ");
  }
  return "";
};

export const hexToRgb = (hexColor) => {
  let r, g, b;
  if (hexColor?.charAt(0) === "#") {
    hexColor = hexColor?.substr(1);
    r = parseInt(hexColor?.substr(0, 2), 16);
    g = parseInt(hexColor?.substr(2, 2), 16);
    b = parseInt(hexColor?.substr(4, 2), 16);
  } else {
    // If it's not in hex format, handle other formats (e.g., rgb, rgba)
    const matchColors = hexColor?.match(/\d+/g);
    if (matchColors.length === 3) {
      [r, g, b] = matchColors.map((color) => parseInt(color, 10));
    } else if (matchColors.length === 4) {
      [r, g, b] = matchColors.slice(0, 3).map((color) => parseInt(color, 10));
    } else {
      return null; // Unable to determine color
    }
  }

  const rgbValue = `rgb(${r}, ${g}, ${b})`;

  return {
    r,
    g,
    b,
    value: rgbValue
  };
}


export const getContrastColor = (backgroundColor, lightBgContrastColor = "#000000", darkBgContrastColor = "#FFFFFF", maxDarkBgLuminance = 0.5) => {
  if (!backgroundColor) {
    return null;
  }
  // Convert hex color to RGB
  let r, g, b;
  if (backgroundColor.charAt(0) === "#") {
    backgroundColor = backgroundColor.substr(1);
    r = parseInt(backgroundColor.substr(0, 2), 16);
    g = parseInt(backgroundColor.substr(2, 2), 16);
    b = parseInt(backgroundColor.substr(4, 2), 16);
  } else {
    // If it's not in hex format, handle other formats (e.g., rgb, rgba)
    const matchColors = backgroundColor.match(/\d+/g);
    if (matchColors.length === 3) {
      [r, g, b] = matchColors.map((color) => parseInt(color, 10));
    } else if (matchColors.length === 4) {
      [r, g, b] = matchColors.slice(0, 3).map((color) => parseInt(color, 10));
    } else {
      return null; // Unable to determine color
    }
  }

  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return contrast color based on luminance
  return luminance > maxDarkBgLuminance ? lightBgContrastColor : darkBgContrastColor;
};

// function getContrastColor(hexColor) {
//     // Remove the hash character if it's included
//     hexColor = hexColor?.replace("#", "");

//     // Convert hex color to RGB
//     const r = parseInt(hexColor?.substring(0, 2), 16);
//     const g = parseInt(hexColor?.substring(2, 4), 16);
//     const b = parseInt(hexColor?.substring(4, 6), 16);

//     // Calculate relative luminance (brightness) using the WCAG formula
//     const brightness = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

//     // Use white font color for dark backgrounds, and black for light backgrounds
//     return brightness > 0.5 ? "#000000" : "#ffffff";
// }

// Developer fallbacks ("GET MENUS ERROR", ...) are only meant for the console, never for a toast.
const isDeveloperMessage = (message) => typeof message === 'string' && /^[A-Z0-9 _/-]+$/.test(message.trim())

const isCancelledRequest = (response) =>
  response?.__CANCEL__ === true || response?.code === 'ERR_CANCELED' || response?.name === 'CanceledError'

const userFacingErrorMessage = (response, defaultMessage) => {
  if (response?.data?.message) return response.data.message
  if (response?.code === 'ERR_NETWORK' || response?.message === 'Network Error')
    return 'Network error. Check your connection and try again.'
  if (defaultMessage && !isDeveloperMessage(defaultMessage)) return defaultMessage
  return 'Something went wrong. Please try again.'
}

export const handleError = (dispatch) => (response, type, defaultMessage, action) => {
  // A request the app cancelled itself (reload, navigation, superseded fetch) is not a user-facing error
  if (isCancelledRequest(response)) {
    if (type) dispatch({ type });
    return;
  }
  if (response?.status !== 401 && response?.status !== 403) {
    if (!response?.data?.message && defaultMessage) console.warn(defaultMessage, response)
    toast.error(userFacingErrorMessage(response, defaultMessage));
  }
  if (response?.status === 401) {
    dispatch({ type: LOGOUT_USER });
  }
  if (response?.status === 403) {
    dispatch(navigateTo('/forbidden'))
    dispatch({
      type: SET_PERMISSIONS,
      payload: response?.data?.permissions
    })
  }
  if (type)
    dispatch({
      type: type,
    });
  if (action && response?.status === 501)
    action();
}

export function deepEqual(obj1, obj2, excludeKeys) {
  // Check if both inputs are arrays
  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) {
      return false;
    }

    // Sort the arrays to ensure the elements are in the same order for comparison
    obj1.sort();
    obj2.sort();

    // Compare each element of the arrays recursively
    for (let i = 0; i < obj1.length; i++) {
      if (!deepEqual(obj1[i], obj2[i], excludeKeys)) {
        return false;
      }
    }

    return true;
  }

  // Check if both inputs are objects
  if (typeof obj1 === 'object' && typeof obj2 === 'object' && obj1 !== null && obj2 !== null) {
    const keys1 = Object.keys(obj1).filter(key => !excludeKeys?.includes(key)).sort();
    const keys2 = Object.keys(obj2).filter(key => !excludeKeys?.includes(key)).sort();

    if (keys1.length !== keys2.length || !deepEqual(keys1, keys2)) {
      return false;
    }

    for (const key of keys1) {
      if (!deepEqual(obj1[key], obj2[key], excludeKeys)) {
        return false;
      }
    }

    return true;
  }

  // If not arrays or objects, compare directly
  return obj1 === obj2;
}

export function isDarkMode(themePreference) {
  if (!themePreference || themePreference === 'light') {
    return false;
  }

  if (themePreference === 'dark') {
    return true;
  }

  if (themePreference === 'system') {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  }

  return false;
}

/**
 * Handles notification click by setting restaurant first (to load pages), then navigating
 * @param {string} clickAction - The click action URL from notification data
 * @param {Function} setSelectedRest - Function to set selected restaurant
 * @param {Object} history - React Router history object for navigation
 * @param {Function} setNotificationOpen - Optional function to open notification panel
 */
export function handleNotificationClick(clickAction, setSelectedRest, history, setNotificationOpen) {
  // Handle special case for viewing notifications
  if (clickAction === 'view-new-notifications') {
    if (setNotificationOpen) {
      setNotificationOpen(true);
    }
    return;
  }

  // Parse URL from clickAction
  let url = '/';
  clickAction.split('/').forEach((str, i) => {
    if (i !== 0 && i !== 1 && i !== 2) {
      url += str + "/";
    }
  });

  // Remove trailing slash if present
  url = url.replace(/\/$/, '');

  // Parse URL parts to determine if restaurant needs to be set
  const urlParts = url.split('/').filter(part => part !== '');

  // Determine restaurant UUID from URL (e.g., /messaging/{restaurantUuid}/...)
  let restaurantUuid = null;
  if (urlParts.length > 0) {
    switch (urlParts[0]) {
      case 'messaging':
        if (urlParts[1]) {
          restaurantUuid = urlParts[1];
        }
        break;
      // Add more cases here as needed for other routes that require restaurant selection
      default:
        break;
    }
  }

  // If restaurant needs to be changed, set it first, then navigate
  if (restaurantUuid && setSelectedRest) {
    // Set the restaurant first (this will trigger Redux update and router re-render)
    setSelectedRest(restaurantUuid);

    // Wait for Redux state to update and router to re-render before navigating
    // Using setTimeout to allow the restaurant change to propagate
    setTimeout(() => {
      if (history) {
        const navPath = url === '/' || url.startsWith('/') ? url : `/${url}`;
        history.push(ensureAdminShellPath(navPath));
      }
    }, 100); // Small delay to ensure restaurant is set and pages are loaded
  } else {
    // No restaurant change needed, navigate immediately
    if (history) {
      const navPath = url === '/' || url.startsWith('/') ? url : `/${url}`;
      history.push(ensureAdminShellPath(navPath));
    }
  }
}

/**
 * Parse filename from Content-Disposition header.
 * @param {string|null} contentDisposition - Content-Disposition header value
 * @returns {string|null} - Extracted filename or null
 */
export function getFileNameFromContentDisposition(contentDisposition) {
  if (!contentDisposition) return null;
  const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
  if (filenameMatch) return filenameMatch[1].trim();
  const simpleMatch = contentDisposition.match(/filename=["']?([^"';]+)["']?/i);
  return simpleMatch ? simpleMatch[1].trim() : null;
}

/**
 * Download an Excel file from response, with optional custom filename suffix.
 * On success: builds filename from response/headers and appends suffix (e.g. "-RestaurantName").
 * On error (e.g. CORS): falls back to opening the original URL without modifying the filename.
 * @param {Object} response - API response with response.data.url (required)
 * @param {string} [fileNameSuffix] - Suffix to append before extension (e.g. restaurant name). If empty, uses default "restaurant".
 * @param {string} [defaultBaseName='importfile.xlsx'] - Default base filename when none can be resolved
 */
export async function downloadExcelWithCustomName(response, fileNameSuffix = 'restaurant', defaultBaseName = 'importfile.xlsx') {
  const url = response?.data?.url;
  if (!url) return;

  const suffix = fileNameSuffix || 'restaurant';

  try {
    const res = await fetch(url);
    const contentDisposition = res.headers.get('Content-Disposition');
    let baseFileName = getFileNameFromContentDisposition(contentDisposition) || response?.data?.fileName;
    if (!baseFileName) {
      const pathMatch = url.match(/\/([^/?#]+)(?:\?|#|$)/);
      baseFileName = pathMatch ? pathMatch[1] : defaultBaseName;
    }
    const ext = baseFileName.includes('.') ? baseFileName.slice(baseFileName.lastIndexOf('.')) : '.xlsx';
    const nameWithoutExt = baseFileName.includes('.') ? baseFileName.slice(0, baseFileName.lastIndexOf('.')) : baseFileName;
    const fileName = `${nameWithoutExt}-${suffix}${ext}`;
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch (err) {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

/**
 * Normalizes any color representation into a valid 7-character #rrggbb hex format
 * suitable for native HTML5 <input type="color">.
 */
export const normalizeHexColor = (color, defaultColor = '#000000') => {
  if (!color || typeof color !== 'string') return defaultColor;
  const c = color.trim();
  if (c.startsWith('#')) {
    if (c.length === 7 && /^#[0-9A-Fa-f]{6}$/.test(c)) return c;
    if (c.length === 4 && /^#[0-9A-Fa-f]{3}$/.test(c)) {
      return `#${c[1]}${c[1]}${c[2]}${c[2]}${c[3]}${c[3]}`;
    }
    if (c.length === 9 && /^#[0-9A-Fa-f]{8}$/.test(c)) {
      return c.substring(0, 7);
    }
    if (c.length === 5 && /^#[0-9A-Fa-f]{4}$/.test(c)) {
      return `#${c[1]}${c[1]}${c[2]}${c[2]}${c[3]}${c[3]}`;
    }
  } else if (c.startsWith('rgb')) {
    const rgbMatch = c.match(/\d+/g);
    if (rgbMatch && rgbMatch.length >= 3) {
      const r = Math.min(255, parseInt(rgbMatch[0], 10)).toString(16).padStart(2, '0');
      const g = Math.min(255, parseInt(rgbMatch[1], 10)).toString(16).padStart(2, '0');
      const b = Math.min(255, parseInt(rgbMatch[2], 10)).toString(16).padStart(2, '0');
      return `#${r}${g}${b}`;
    }
  } else if (/^[0-9A-Fa-f]{6}$/.test(c)) {
    return `#${c}`;
  } else if (/^[0-9A-Fa-f]{3}$/.test(c)) {
    return `#${c[0]}${c[0]}${c[1]}${c[1]}${c[2]}${c[2]}`;
  }
  return defaultColor;
};

function nestedCountry(entity) {
  if (!entity || typeof entity !== "object") return null;
  return entity.country || entity.Country || null;
}

function countryNameCandidates(country, language) {
  const raw = country?.countryName;
  if (typeof raw === "string") return raw.trim() ? [raw.trim()] : [];
  if (!raw || typeof raw !== "object") return [];
  const ordered = [language, "english", ...Object.keys(raw)];
  const names = [];
  ordered.forEach((key) => {
    const value = raw[key];
    if (typeof value === "string" && value.trim() && !names.includes(value.trim())) names.push(value.trim());
  });
  return names;
}

/** Country label that matches the global country list for the active branch. */
export function resolveDefaultAddressCountryName({ branch, restaurant, restaurants, countries, language } = {}) {
  const branchRestaurant = branch?.restaurant || branch?.Restaurant || null;
  const restaurantUuid = restaurant?.uuid || branchRestaurant?.uuid;
  const restaurantFromList = restaurantUuid && Array.isArray(restaurants)
    ? restaurants.find((row) => row?.uuid === restaurantUuid) || null
    : null;
  const country = nestedCountry(branch)
    || nestedCountry(branchRestaurant)
    || nestedCountry(restaurant)
    || nestedCountry(restaurantFromList);
  const options = Array.isArray(countries) ? countries.filter(Boolean) : [];

  if (country?.code) {
    const byCode = options.find((row) => String(row?.code || "").toUpperCase() === String(country.code).toUpperCase());
    if (typeof byCode?.countryName === "string" && byCode.countryName.trim()) return byCode.countryName.trim();
  }
  if (country?.phone != null && String(country.phone).trim() !== "") {
    const byPhone = options.find((row) => String(row?.phone) === String(country.phone));
    if (typeof byPhone?.countryName === "string" && byPhone.countryName.trim()) return byPhone.countryName.trim();
  }
  for (const name of countryNameCandidates(country, language)) {
    const byName = options.find((row) => String(row?.countryName || "").trim().toLowerCase() === name.toLowerCase());
    if (typeof byName?.countryName === "string" && byName.countryName.trim()) return byName.countryName.trim();
  }
  return countryNameCandidates(country, language)[0] || "";
}